import { TargetSchema } from "@/types";
import type { CoverageAnalysis } from "@/types/documentTransformation";

/**
 * Does this document's claim-line mapping give the deductible basis what it needs?
 * (#7380, ADR-081 D5.1 cash basis.)
 *
 * A claim line counts toward the deductible on the day it was paid. A line with a
 * paid amount and no paid date can never be placed in a paid window, so it never
 * counts, and the PO ruled (#7245, 2026-10-09) that no stand-in date is acceptable.
 * A file that maps no paid amount at all loads every line as Submitted (loader
 * rule, !4847), so those lines count nowhere either. Both are mapping problems a
 * reviewer can fix BEFORE approving, which is where this verdict is shown: the
 * "Before you approve" rail (card with Open field mapping) and the Mapping tab.
 *
 * Inputs are the template's field mappings (what the file maps) and the
 * transformation's coverage analysis (how many lines each field was empty on). The
 * mapping decides the shape; coverage supplies the count. Pure, so every shape is
 * unit-tested without rendering.
 */
export type PaidDateCoverage =
  | { kind: "ok" }
  | {
      kind: "missing_paid_date";
      /** Lines with no paid date; null when neither coverage nor a line count is available. */
      count: number | null;
    }
  | { kind: "missing_paid_amount" };

export interface PaidDateCoverageInput {
  /** The applied mapping: one entry per (schema, target field) the template maps. */
  mappedFields: ReadonlyArray<{ schema: TargetSchema; field: string }>;
  coverage: CoverageAnalysis | null | undefined;
  /** Claim lines in the document, when known (the server's per-schema count, or the loaded count when the whole file is loaded). */
  lineCount: number | null;
}

/**
 * Null when the document has no claim lines to judge (a reimbursement or
 * claimant-only file): nothing to warn about and no pass line either.
 */
export const assessPaidDateCoverage = ({
  mappedFields,
  coverage,
  lineCount,
}: PaidDateCoverageInput): PaidDateCoverage | null => {
  const mapped = new Set(mappedFields.filter((m) => m.schema === TargetSchema.CLAIM_ITEM).map((m) => m.field));
  const claimCoverage = coverage?.schema_analyses?.find((a) => a.target_schema === TargetSchema.CLAIM_ITEM);
  if (mapped.size === 0 && !claimCoverage) return null;

  const unpopulated = (field: string): number | null => {
    const n = claimCoverage?.unpopulated_fields?.[field];
    return typeof n === "number" && Number.isFinite(n) ? n : null;
  };
  // "Populated" = mapped and not empty on every line. With a line count we can tell
  // a mapped-but-always-empty column (a date the transformer could not read on any
  // row) from a mapped one; without it, a mapped column is taken as populated.
  const populated = (field: string): boolean => {
    const empties = unpopulated(field);
    if (mapped.size > 0) {
      if (!mapped.has(field)) return false;
      return !(lineCount !== null && lineCount > 0 && empties !== null && empties >= lineCount);
    }
    // No template to read (automatic extraction): infer from coverage alone. A
    // field absent from unpopulated_fields was populated on every line.
    if (empties === null) return true;
    return lineCount !== null && lineCount > 0 ? empties < lineCount : false;
  };

  // "No paid amount" is a MAPPING verdict: a mapped column that is blank on every
  // line still loads its rows as Paid at $0 (loadedClaimStatusDefault keys on the
  // column being mapped), so those lines go on to the paid-date check like any
  // other. Only when nothing maps to paid_amount do the lines load as Submitted.
  // Without a template to read, coverage alone cannot tell the two apart and the
  // unmapped reading stands.
  const amountMapped = mapped.size > 0 ? mapped.has("paid_amount") : populated("paid_amount");
  if (!amountMapped) return { kind: "missing_paid_amount" };
  if (populated("paid_date")) return { kind: "ok" };
  const empties = unpopulated("paid_date");
  return { kind: "missing_paid_date", count: empties ?? lineCount };
};
